/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { AddToCartBehavior } from '@msdyn365-commerce/components';
import MsDyn365, { getUrlSync } from '@msdyn365-commerce/core';
import { ICartIconViewProps, ICartViewProps, IFlyoutCartLineItemViewProps } from '@msdyn365-commerce-modules/cart';
import { Button, getPayloadObject, INodeProps, KeyCodes, Modal, Node, onTelemetryClick } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import React from 'react';
import { ICartIconProps as ICartIconExtensionProps } from '../definition-extensions/cart-icon.ext.props.autogenerated';
import { CommerceProperty } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';

/**
 * ICartIconViewState: Interface for Cart Icon View State.
 */
interface ICartIconViewState {
    isModalOpen: boolean;
}

interface ICartIconExtraProps {
    cartData?: any; // AsyncResult<ICartState>
    onUpdateQuantity?: (cartLineId: string, newQty: number) => Promise<void>;
    config: {
        showCheckoutBtn: boolean;
    };
}

/**
 * Render Cart lines.
 * @param cartLines - Flyout cart line view props.
 * @param props - Cart icon view props.-
 * @returns JSX Element.
 */
const renderCartlines = (
    cartLines: IFlyoutCartLineItemViewProps[] | undefined,
    props: ICartIconViewProps & ICartIconExtraProps
): JSX.Element[] | null => {
    if (!cartLines) {
        props.context.telemetry.error('Cartlines content is empty, module wont render');
        return null;
    }

    return cartLines.map((cartLine, index) => {
        const cartLineIndex = index;
        // If the cartline content is a valid React element, clone it and add a flag so
        // the CartLineItem component knows it's being rendered inside the mini-cart/flyout.
        const cartlineContent = React.isValidElement(cartLine.cartline)
            ? React.cloneElement(cartLine.cartline as React.ReactElement, {
                  isRenderedInMiniCart: true,
                  quantityOnChange: props.onUpdateQuantity,
                  cartData: props.cartData,
                  remove: React.isValidElement(cartLine.remove)
                      ? React.cloneElement(cartLine.remove, {
                            children: undefined,
                            title: undefined
                        })
                      : null
              })
            : cartLine.cartline;

        const isItemGWPAddedAutomatically = cartLine.data?.cartline?.ExtensionProperties?.some(
            (prop: CommerceProperty) => prop.Key === 'GWPType' && prop.Value?.StringValue === 'AUTO'
        )
            ? true
            : false;
        const isItemFree = cartLine.data?.cartline?.TotalAmount === 0;

        return (
            <Node {...props.miniCartItemWrapper} key={cartLineIndex}>
                <Node
                    className={classnames(
                        cartLine.storeLocation ? 'msc-cart-line__set-net-price' : '',
                        isItemFree || isItemGWPAddedAutomatically ? 'free-item' : ''
                    )}
                >
                    {cartlineContent}
                </Node>
                {cartLine.storeLocation}
            </Node>
        );
    });
};

/**
 * Initiate total price.
 * @param props - Cart icon view props.
 * @returns JSX Element.
 */
const renderTotalPrice = (props: ICartIconViewProps & ICartIconExtensionProps<{}>): JSX.Element | null => {
    const cart = props.data.cart.result ?? undefined;
    const price = cart && !cart.hasInvoiceLine && (cart.cart.TotalAmount || undefined);
    const totalPrice = price
        ? props.context.cultureFormatter.formatCurrency(price)
        : props.context.cultureFormatter.formatCurrency((props.resources as any).emptyPrice);
    return cart && !cart.hasInvoiceLine ? (
        <div className='ms-cart-icon__subTotalText'>
            <span className='ms-cart-icon__subTotalPriceText'>
                {props.resources.totalPriceFormatString}&nbsp;
                <span className='ms-cart-icon__subTotalPrice'>{totalPrice}</span>
            </span>
        </div>
    ) : (
        <></>
    );
};

/**
 * Initiate subtotal message.
 * @param props - Cart icon view props.
 * @returns JSX Element.
 */
const renderSubtotalMessage = (props: ICartIconViewProps & ICartIconExtensionProps<{}>): JSX.Element | null => {
    return null;
    // const cart = props.data.cart.result ?? undefined;
    // return cart && !cart.hasInvoiceLine ? <p className='ms-cart-icon__subtotal-message'>{props.resources.subTotalMessage}</p> : null;
};

/**
 *
 * CartIconView component.
 * @extends {React.PureComponent<ICartIconViewProps>}
 */
export class CartIconView extends React.PureComponent<
    ICartViewProps & ICartIconViewProps & ICartIconExtensionProps<{}> & ICartIconExtraProps,
    ICartIconViewState
> {
    private isAutoDisplayTriggered: boolean;

    private readonly cartIconRef: React.RefObject<HTMLDivElement> = React.createRef();

    public constructor(
        props: ICartViewProps & ICartIconViewState & ICartIconViewProps & ICartIconExtensionProps<{}> & ICartIconExtraProps
    ) {
        super(props);
        this.isAutoDisplayTriggered = false;
        this.state = {
            isModalOpen: false
        };
    }

    public componentDidMount(): void {
        if (MsDyn365.isBrowser) {
            window.addEventListener('keydown', this._escFunction, false);
        }
    }

    public componentWillUnmount(): void {
        if (MsDyn365.isBrowser) {
            window.removeEventListener('keydown', this._escFunction, false);
        }
    }

    public componentDidUpdate(
        prevProps: Readonly<ICartViewProps & ICartIconViewProps & ICartIconExtensionProps<{}>>,
        prevState: Readonly<ICartIconViewState>
    ): void {
        // Track ContentSquare artificial pageviews when mini-cart opens/closes
        if (MsDyn365.isBrowser && prevState.isModalOpen !== this.state.isModalOpen) {
            if (this.state.isModalOpen) {
                this._sendCsPopinOpenArtificialPV();
            } else {
                this._sendCsPopinCloseArtificialPV();
            }
        }
    }

    /**
     * Render Cart Item count.
     * @returns JSX Element.
     */
    public render(): JSX.Element | null {
        const cart = this.props.data.cart.result ?? undefined;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- check config.
        const shouldShowMiniCart =
            cart?.isProductAddedToCart && this.props.context.app.config.addToCartBehavior === AddToCartBehavior.showMiniCart;
        if (shouldShowMiniCart) {
            if (!this.isAutoDisplayTriggered) {
                // First time trigger auto mini cart.
                this.setState({ isModalOpen: true });
                this.isAutoDisplayTriggered = true;
            } else if (!this.state.isModalOpen) {
                // If modal is closed, reset the flag.
                this.isAutoDisplayTriggered = false;
            }
        }

        return (
            <Node {...this.props.miniCartWrapper} {...this.props.renderModuleAttributes(this.props)}>
                <div
                    onClick={this._openModal}
                    className='ms-cart-icon-wrapper msc-btn'
                    ref={this.cartIconRef}
                    style={{ cursor: 'pointer' }}
                >
                    {this._renderCartIconContent()}
                </div>
                <Node {...this._modalContainer()}>
                    <Node className='ms-cart-icon__header msc-modal__header'>
                        {this.props.flyoutTitle}
                        {this._renderCartItemCount(this.props)}
                        {this._renderCartCloseIcon()}
                    </Node>
                    <Node className='ms-cart-icon__body msc-modal__body'>
                        {this.props.data.cart.result?.isEmpty ? (
                            <>
                                {this.props.slots.emptyCart}
                                <div className='ms-cart-icon__body__empty-cart'>
                                    {/* <div className='ms-cart-icon__body__empty-cart-icon' /> */}
                                    <p className='ms-cart-icon__body__empty-cart-text'>{this.props.resources.emptyCartText}</p>
                                </div>
                            </>
                        ) : null}
                        {this.props.checkoutBlockedDueToUnavailableFunds}
                        <Node {...this.props.CartlinesWrapper}>{renderCartlines(this.props.cartlines, this.props)}</Node>
                    </Node>
                    <Node className='ms-cart-icon__footer msc-modal__footer'>
                        {this.props.slots.promoContentItem}
                        {renderTotalPrice(this.props)}
                        {renderSubtotalMessage(this.props)}
                        {/* {this.props.checkoutAsSignInUserButton}
                        {this.props.checkoutAsGuestButton} */}
                        {this.props.config.showCheckoutBtn ? (
                            <>
                                {React.isValidElement(this.props.checkoutAsSignInUserButton)
                                    ? React.cloneElement(this.props.checkoutAsSignInUserButton, {
                                          title: this.props.resources.minicartCheckoutButtonTitle || this.props.title,
                                          children: this.props.resources.minicartCheckoutButtonTitle || this.props.children
                                      })
                                    : null}
                                {this.props.config.isAnonymousCheckout && React.isValidElement(this.props.checkoutAsGuestButton)
                                    ? React.cloneElement(this.props.checkoutAsGuestButton, {
                                          title: this.props.resources.minicartCheckoutButtonTitle || this.props.title,
                                          children: this.props.resources.minicartCheckoutButtonTitle || this.props.children
                                      })
                                    : null}
                                {!this.props.data.cart.result?.isEmpty && (
                                    <div className='ms-cart-icon__cart-message'>{this.props.resources.minicartGiftCouponMessage}</div>
                                )}
                            </>
                        ) : null}
                        {this.props.data.cart.result?.isEmpty
                            ? null
                            : React.isValidElement(this.props.goToCartButton)
                            ? React.cloneElement(this.props.goToCartButton, {
                                  className: classnames(
                                      (this.props.goToCartButton as React.ReactElement).props?.className,
                                      !this.props.config.showCheckoutBtn ? 'solid' : undefined
                                  )
                              })
                            : this.props.goToCartButton}
                        <Node className='ms-cart-icon__btn-section'>
                            {this.props.data.cart.result?.isEmpty ? (
                                <Button
                                    className='ms-cart-icon__btn-backtoshopping'
                                    href={getUrlSync('home', this.props.context.actionContext)}
                                >
                                    {this.props.resources.continueShoppingButtonTitle}
                                </Button>
                            ) : null}
                        </Node>
                    </Node>
                </Node>
            </Node>
        );
    }

    /**
     * Initiate modal container.
     * @returns Inode props.
     */
    private readonly _modalContainer = (): INodeProps => {
        return {
            tag: Modal,
            placement: 'bottom-end',
            hideArrow: true,
            className: 'ms-cart-icon__minicartmodal-container',
            wrapClassName: 'ms-cart-icon__minicartmodal',
            isOpen: this.state.isModalOpen,
            toggle: this.closeModal
        };
    };

    /**
     * Initiate open modal.
     */
    private readonly _openModal = (event: React.MouseEvent): void => {
        event?.preventDefault?.();
        event?.stopPropagation?.();

        const payLoad = getPayloadObject('click', this.props.telemetryContent!, 'cart-icon', '');
        onTelemetryClick(this.props.telemetryContent!, payLoad, 'cart-icon');
        this.setState({
            isModalOpen: true
        });
    };

    /**
     * Send ContentSquare artificial pageview for popin open
     */
    private readonly _sendCsPopinOpenArtificialPV = (): void => {
        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const w: any = window;
            w._uxa = w._uxa || [];
            const customQuery = 'minicart_open';
            const csQuery = (location.search || '') + (location.search ? '&' : '?') + customQuery;
            w._uxa.push(['setQuery', csQuery]);
            w._uxa.push(['trackPageview', window.location.pathname + window.location.hash.replace('#', '?__')]);
            w._uxa.push(['setQuery', location.search || '']);
        } catch (e) {
            console.error('[ContentSquare] Error sending open artificial pageview', e);
        }
    };

    /**
     * Initiate close modal.
     */
    private readonly closeModal = (): void => {
        this.cartIconRef.current?.focus();
        this.setState({
            isModalOpen: false
        });
    };

    /**
     * Send ContentSquare artificial pageview for popin close
     */
    private readonly _sendCsPopinCloseArtificialPV = (): void => {
        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const w: any = window;
            w._uxa = w._uxa || [];
            w._uxa.push(['setQuery', location.search || '']);
            w._uxa.push(['trackPageview', window.location.pathname + window.location.hash.replace('#', '?__')]);
        } catch (e) {
            console.error('[ContentSquare] Error sending close artificial pageview', e);
        }
    };

    /**
     * Initiate cart close Icon.
     * @returns JSX Element.
     */
    private readonly _renderCartCloseIcon = (): JSX.Element | null => {
        return <Button className='ms-cart-icon__btn-close' aria-label='Close' onClick={this.closeModal} />;
    };

    /**
     * Render Cart Item count.
     * @param props - Cart icon view props.
     * @returns JSX Element.
     */
    private readonly _renderCartItemCount = (props: ICartIconViewProps & ICartIconExtensionProps<{}>): JSX.Element | null => {
        const defaultCartItemCount: number = 0;

        const cartItemlength = props.data.cart.result ? props.data.cart.result.totalItemsInCart : defaultCartItemCount;
        const cartItemCount = `${cartItemlength} ${props.resources.items}`;

        return <Node className='ms-cart-icon__count'>{cartItemCount}</Node>;
    };

    /**
     * Render cart icon content without any link wrapper.
     * @returns JSX Element.
     */
    private readonly _renderCartIconContent = (): React.ReactNode => {
        const cartIcon = this.props.cartIcon;

        if (React.isValidElement(cartIcon) && cartIcon.type === 'a') {
            return cartIcon.props.children;
        }

        if (typeof cartIcon === 'object' && cartIcon !== null) {
            return cartIcon;
        }

        return cartIcon;
    };

    /**
     * Handle escape click to close modal.
     * @param event - On press of any key.
     */
    private readonly _escFunction = (event: KeyboardEvent) => {
        if (event.keyCode === KeyCodes.Escape && this.state.isModalOpen) {
            this.closeModal();
        }
    };
}

export default CartIconView;
